import pLimit from "p-limit";
import { useCallback, useRef, useState, useEffect } from "react";
import { Id, toast } from "react-toastify";
import { fetchItem, postItem } from "../../queries/queries-helper";
import ResponseApi from "../../models/interfaces/response-api";
import { JobAdapter, UnifiedJobStatus } from "../../models/interfaces/dtos/job";
import { useOptionalProjectProcessContext } from "@/services/context/project-process/use-project-process-context";
import pollJob from "./poll-job";

export interface JobStatusMessage {
    pending?: string | ((current: number, total: number) => string);
    success?: string;
    error?: string | ((index: number) => string);
}

interface UseJobsAsyncOptions<TJob> {
    jobsUrl: string[];
    jobBody?: unknown;
    getStatusUrl: (jobId: string, job: TJob) => string;
    adapter: JobAdapter<TJob>;
    onJobDone?: (job: TJob, status: TJob, jobUrl: string, jobId: string, toastId: Id, index: number) => Promise<string | void> | string | void;
    allJobsFinished?: (succeeded: boolean) => Promise<void> | void;
    onError?: (index: number) => void;
    jobStatusMessage?: JobStatusMessage;
}

const useJobsAsync = <TJob,>(options: UseJobsAsyncOptions<TJob>) => {
    const processContext = useOptionalProjectProcessContext();
    const beginProcess = processContext?.beginProcess;
    const [isPolling, setIsPolling] = useState(false);
    const isMounted = useRef(true);
    const isRunning = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const launchJobs = useCallback(async (): Promise<boolean> => {
        if (isRunning.current || !isMounted.current) return false;
        isRunning.current = true;
        setIsPolling(true);
        const finishProcess = beginProcess?.();
        const limit = pLimit(2);

        try {
            const results = await Promise.allSettled(options.jobsUrl.map((jobUrl, index) => limit(async () => {
                if (!isMounted.current) return false;
                const pending = options.jobStatusMessage?.pending;
                const toastId = toast.loading(typeof pending === "function"
                    ? pending(index + 1, options.jobsUrl.length)
                    : pending ?? `Processus ${index + 1} sur ${options.jobsUrl.length} en cours...`);

                try {
                    const response = await postItem<ResponseApi<TJob>>(jobUrl, options.jobBody);
                    if (!response?.success) throw new Error(response?.errorMessages?.[0] ?? "Le lancement du job a échoué.");
                    const job = response.entity;
                    const jobId = options.adapter.getId(job);
                    const status = await pollJob(
                        () => fetchItem<TJob>(options.getStatusUrl(jobId, job)),
                        options.adapter.getStatus,
                        () => isMounted.current,
                    );
                    if (!status) return false;
                    if (options.adapter.getStatus(status) !== UnifiedJobStatus.Success) {
                        const message = options.jobStatusMessage?.error;
                        throw new Error(typeof message === "function" ? message(index) : message ?? "Une erreur est survenue.");
                    }

                    const successMessage = await options.onJobDone?.(job, status, jobUrl, jobId, toastId, index);
                    if (!isMounted.current) return false;
                    toast.update(toastId, {
                        render: successMessage ?? options.jobStatusMessage?.success ?? "Travail terminé.",
                        type: "success", isLoading: false, autoClose: 5000, closeButton: true,
                    });
                    return true;
                } catch (error) {
                    if (isMounted.current) {
                        toast.update(toastId, {
                            render: error instanceof Error ? error.message : "Une erreur est survenue.",
                            type: "error", isLoading: false, autoClose: false, closeButton: true,
                        });
                        options.onError?.(index);
                    }
                    return false;
                } finally {
                    if (!isMounted.current) toast.dismiss(toastId);
                }
            })));

            const succeeded = results.length > 0 && results.every(result => result.status === "fulfilled" && result.value);
            if (!isMounted.current) return false;
            // Attendre aussi l'import final et les éventuels traitements enchaînés.
            await options.allJobsFinished?.(succeeded);
            return succeeded;
        } catch (error) {
            if (isMounted.current) toast.error(error instanceof Error ? error.message : "La finalisation du traitement a échoué.");
            return false;
        } finally {
            finishProcess?.();
            isRunning.current = false;
            if (isMounted.current) setIsPolling(false);
        }
    }, [options, beginProcess]);

    return { launchJobs, isPolling };
};

export default useJobsAsync;
